// Import dependencies
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import { initialiser } from '../../ramen-imports/utilitybelt';
import { addClasses, removeClasses } from '../../utils';
import doesFileExist from '../../utils/doesFileExist';

const defaultSettings = {
  optionSelectedClass: 'is-selected',
  contentShowClass: 'is-active',
};

class TicketOptions {
  constructor(config) {
    const { el, settings } = config;

    this.container = el;
    this.swiperInstance = null;
    this.options = [...el.querySelectorAll(settings.options)];
    this.optionsContentList = [...el.querySelectorAll(settings.optionsContent)];
    this.isSelected = false;
    this.instructions = el.querySelector('.js-ticket-options-instructions');
    this.isTabsBlock = this.container.classList.contains('js-ticket-options-tabs');

    this.settings = { ...defaultSettings, ...settings };

    // API mode specific
    this.apiMode = this.container.classList.contains('js-ticket-options-api');
    this.eventIds = this.container.dataset.eventids;
    this.loading = false;
    this.swiperWrapper = this.container.querySelector('.swiper-wrapper');
    this.ticketData = [];
    this.unavailableText = 'Unavailable';
  }

  init = async () => {
    if (this.apiMode) {
      await this.fetchProductData();
    } else {
      setTimeout(() => this.removeInstructions(), 10000);

      if (!this.isTabsBlock) this.switchToAvailable();
      this.changeContent();
    }

    this.initSwiper();
    this.handleInstructions();
    this.bindEvents();
  };

  initSwiper = () => {
    // Ensure modules are set
    if (this.settings.swiperSettings) {
      this.settings.swiperSettings.modules = [Navigation];
    }
    this.swiperInstance = new Swiper(this.settings.slider, this.settings.swiperSettings);
  };

  handleInstructions = () => {
    const slides = [...this.container.querySelectorAll('.swiper-slide')];
    const totalSlidesWidth = slides.reduce((acc, cur) => cur.offsetWidth + acc, 0);

    if (this.container.offsetWidth >= totalSlidesWidth) this.removeInstructions();
  };

  bindEvents = () => {
    this.options.forEach((option) => {
      option.addEventListener('click', async (e) => {
        const { selector } = e.currentTarget.dataset;

        if (this.apiMode) await this.fetchProductData(false);
        this.showContent(selector);
      });
    });

    if (!this.isTabsBlock) this.instructions.addEventListener('click', () => this.removeInstructions());
  };

  fetchProductData = async (switchToAvailable = true) => {
    this.loading = true;
    this.swiperWrapper.classList.add('swiper-wrapper--loading');

    const jsonFilePath = '/uploads/ticket-availability.json';
    const jsonFileExists = await doesFileExist(jsonFilePath);

    const res = await fetch(jsonFileExists ? jsonFilePath : `/api/ticket-availability?eventIds=${this.eventIds}`);

    if (!res.ok) {
      this.swiperWrapper.insertAdjacentHTML(
        'beforeend',
        '<span class="c-ticket-options__error">Error fetching data</span>',
      );
    }

    res.json().then((ticketData) => {
      this.updateSlides(ticketData, switchToAvailable);
      this.loading = false;

      this.swiperWrapper.classList.remove('swiper-wrapper--loading');
      this.instructions.classList.remove('u-hide');

      setTimeout(() => this.removeInstructions(), 10000);

      this.changeContent();
    });
  };

  showContent = (currentSelector) => {
    this.options.forEach((option) => {
      if (option.dataset.selector === currentSelector) {
        addClasses(option, this.settings.optionSelectedClass);
        return;
      }

      removeClasses(option, this.settings.optionSelectedClass);
    });

    this.optionsContentList.forEach((contentEle) => {
      if (contentEle.id === currentSelector) {
        contentEle.style.display = 'block'; // eslint-disable-line
        setTimeout(() => {
          addClasses(contentEle, this.settings.contentShowClass);
        }, 300);
        return;
      }

      removeClasses(contentEle, this.settings.contentShowClass);

      // Hide element once the animation on the element is complete
      setTimeout(() => {
        contentEle.style.display = 'none'; // eslint-disable-line
      }, 300);
    });
  };

  updateSlides = (ticketData, switchToAvailable) => {
    let slideSelected = false;

    ticketData.forEach((ticket) => {
      if (!ticket.useFallback) {
        const tmStatusMappings = {
          TICKETS_NOT_AVAILABLE: 'c-ticket-options__status c-ticket-options__status--sold-out',
          TICKETS_AVAILABLE: 'c-ticket-options__status c-ticket-options__status--sale',
          FEW_TICKETS_LEFT: 'c-ticket-options__status c-ticket-options__status--selling-fast',
          UNKNOWN: 'c-ticket-options__status c-ticket-options__status--other',
        };

        const tmStatusClass = tmStatusMappings[ticket.statusCode];
        const slide = document.getElementById(ticket.eventId);

        if (slide) {
          const slideTitle = slide.querySelector('.js-ticket-option-title');
          const { defaultText } = slideTitle.dataset;
          const slideTitleSpan = slideTitle.querySelector('span');
          const slideStatus = document.getElementById(`status-${ticket.eventId}`);
          const button = document.getElementById(`button-${ticket.eventId}`);

          if (ticket.showLink) {
            slideTitleSpan.innerText = defaultText;

            if (switchToAvailable && !slideSelected) {
              this.switchOpenClass(slide);

              slideSelected = true;
            }
          } else {
            slideTitleSpan.innerText = this.unavailableText;

            if (button) {
              const { disabledText } = button.dataset;
              button.querySelector('span').innerText = disabledText;
            }
          }

          if (tmStatusClass) {
            slideStatus.innerText = ticket.status;
            slideStatus.className = tmStatusClass;
          }
        }
      }
    });

    if (switchToAvailable
      && !slideSelected
      && ticketData.filter((ticket) => !ticket.useFallback).length < this.options.length
    ) {
      slideSelected = this.switchToAvailable();
    }
  };

  switchToAvailable = () => {
    const availableTicket = this.findAvailableTicket();

    if (availableTicket) {
      this.switchOpenClass(availableTicket);

      return true;
    }

    return false;
  };

  changeContent = () => {
    const firstSelector = this.options[0].dataset.selector;

    this.options.forEach((option) => {
      if (option.classList.contains(this.settings.optionSelectedClass)) {
        this.isSelected = true;
        this.showContent(option.dataset.selector);
      }
    });

    if (!this.isSelected) this.showContent(firstSelector);
  };

  findAvailableTicket = () => {
    const availableTicket = this.options.filter((slide) => (
      slide.querySelector('.c-ticket-selector__pass-title').innerText !== this.unavailableText
    ));

    if (availableTicket.length > 0) {
      return availableTicket[0];
    }

    return null;
  };

  switchOpenClass = (slide) => {
    this.options.forEach((currentOption) => currentOption.classList.remove(this.settings.optionSelectedClass));
    const slideOption = slide.classList.contains('js-ticket-option') ? slide : slide.querySelector('.js-ticket-option');
    slideOption.classList.add(this.settings.optionSelectedClass);
  };

  removeInstructions = () => {
    if (this.instructions) this.instructions.remove();
  };
}

export default (settings) => initialiser(settings, TicketOptions);
